import { create } from 'zustand';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DevicePassport, Role, ServiceAccessResponse, ServicePassword } from '@ste/shared';
import { apiFetch, rootFetch, useAuth } from './auth.ts';
import { useConnection } from './connection.ts';

// Service access (ADR-0014): the owner enters the site's service password and gets an access
// token with installer rights for this one site for an hour. The refresh token stays the same,
// so the next refresh returns to the normal owner session.

interface Elevation {
  /** cloud: site id; gateway: "local" */
  site: string;
  /** the elevated access token; any other token (after a refresh) means: no longer elevated */
  token: string;
  until: number;
}

interface ServiceAccessState {
  elevation: Elevation | undefined;
  set(elevation: Elevation | undefined): void;
}

let expiryTimer: ReturnType<typeof setTimeout> | undefined;

export const useServiceAccess = create<ServiceAccessState>((set) => ({
  elevation: undefined,
  set(elevation) {
    clearTimeout(expiryTimer);
    if (elevation)
      expiryTimer = setTimeout(() => set({ elevation: undefined }), elevation.until - Date.now());
    set({ elevation });
  },
}));

// logout or a refresh (new access token) ends the elevation
useAuth.subscribe((state) => {
  const { elevation } = useServiceAccess.getState();
  if (elevation && state.accessToken !== elevation.token) useServiceAccess.getState().set(undefined);
});

const currentSite = () => {
  const { mode, siteId } = useConnection.getState();
  return mode === 'cloud' ? (siteId ?? '') : 'local';
};

/** the role on the current site, raised to installer while the service access is open */
export function elevatedRole(base: Role | undefined, elevation: Elevation | undefined, site: string) {
  const active = elevation && elevation.site === site && elevation.until > Date.now();
  return active && base === 'owner' ? 'installer' : base;
}

export function useElevation(): Elevation | undefined {
  const elevation = useServiceAccess((s) => s.elevation);
  const site = useConnection((s) => (s.mode === 'cloud' ? (s.siteId ?? '') : 'local'));
  return elevation?.site === site ? elevation : undefined;
}

export function useServiceAccessCommands() {
  const client = useQueryClient();
  return {
    unlock: useMutation({
      mutationFn: (password: string) =>
        apiFetch<ServiceAccessResponse>('/service-access', {
          method: 'POST',
          body: JSON.stringify({ password }),
        }),
      onSuccess: (res) => {
        const until = Date.now() + (res.expiresIn - 30) * 1000;
        useServiceAccess.getState().set({ site: currentSite(), token: res.accessToken, until });
        useAuth.setState({ accessToken: res.accessToken, expiresAt: until });
        void client.invalidateQueries();
      },
    }),
    /** back to the normal session right away */
    lock: () => {
      useServiceAccess.getState().set(undefined);
      useAuth.setState({ expiresAt: 0 });
      void client.invalidateQueries();
    },
  };
}

// ---- STE admins: the service password of a site (STE Connect only) ----

export const useServicePassword = (siteId: string | undefined, enabled: boolean) =>
  useQuery({
    queryKey: ['cloud', 'service-password', siteId],
    queryFn: () => rootFetch<ServicePassword>(`/sites/${siteId}/service-password`),
    enabled: enabled && siteId !== undefined,
    staleTime: Infinity,
  });

export function useSetServicePassword(siteId: string | undefined) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (password: string | undefined) =>
      rootFetch<ServicePassword>(`/sites/${siteId}/service-password`, {
        method: 'PUT',
        body: JSON.stringify(password ? { password } : {}),
      }),
    onSuccess: (data) => client.setQueryData(['cloud', 'service-password', siteId], data),
  });
}

/** device passport of the site's Pi (ADR-0016), STE admins only, loaded on demand */
export const useDevicePassport = (siteId: string | undefined, enabled: boolean) =>
  useQuery({
    queryKey: ['cloud', 'passport', siteId],
    queryFn: () => rootFetch<DevicePassport>(`/sites/${siteId}/device/passport`),
    enabled: enabled && siteId !== undefined,
    staleTime: Infinity,
  });
